import {
  LuArrowRight,
  LuCalendarDays,
  LuChevronDown,
  LuCombine,
  LuGraduationCap,
  LuLayoutGrid,
  LuLeaf,
  LuMenu,
  LuScanLine,
  LuX,
} from 'react-icons/lu'
import { useState, useEffect, useRef, useCallback } from 'react'
import { NavLink, Link } from 'react-router'
import { Container } from '@/components/common'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/cn'
import logo from '@/assets/logo.jpg'
import { kandakePortrait } from '@/assets/siteMedia'

const services = [
  {
    label: '3 Month Integrated Health Programme',
    path: ROUTES.WORK_WITH_ME,
    desc: 'Personalised support over 12 weeks',
    icon: LuCalendarDays,
  },
  {
    label: 'Consultations',
    path: ROUTES.SERVICES_CONSULTATIONS,
    desc: 'One-to-one herbal medicine consultations',
    icon: LuLeaf,
  },
  {
    label: 'Bio-Scan Health Assessment',
    path: ROUTES.SERVICES_SCANS,
    desc: 'Scan, report, feedback and first steps',
    icon: LuScanLine,
  },
  {
    label: 'Integrated Health Consultation + Bio-Scan',
    path: ROUTES.SERVICES_INTEGRATED_HEALTH,
    desc: 'Consultation, scan, reports and follow-up',
    icon: LuCombine,
  },
  {
    label: 'Courses & Training',
    path: ROUTES.SERVICES_COURSES,
    desc: 'Professional herbal education programmes',
    icon: LuGraduationCap,
  },
]

const mainNav = [
  { label: "Women's Health", path: ROUTES.WOMENS_HEALTH },
  { label: 'Products', path: ROUTES.PRODUCTS },
  { label: 'Books', path: ROUTES.BOOKS },
  { label: 'About', path: ROUTES.ABOUT },
  { label: 'Contact', path: ROUTES.CONTACT },
]

// Desktop link: no pill backgrounds — a green underline grows in on hover and stays on the active page
const desktopLink = (active: boolean) =>
  cn(
    'relative flex items-center gap-1 px-3.5 py-2.5 text-base font-medium tracking-wide transition-colors',
    'after:absolute after:inset-x-3.5 after:-bottom-0.5 after:h-0.5 after:origin-left after:rounded-full after:bg-green-600 after:transition-transform after:duration-300',
    active
      ? 'text-green-700 after:scale-x-100'
      : 'text-base-600 hover:text-base-900 after:scale-x-0 hover:after:scale-x-100',
  )

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const mobilePanelRef = useRef<HTMLDivElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>(null)

  // Close mobile menu on browser back/forward — setState in a callback satisfies the lint rule
  const closeMenus = useCallback(() => {
    setMobileOpen(false)
    setMobileServicesOpen(false)
  }, [])

  useEffect(() => {
    window.addEventListener('popstate', closeMenus)
    return () => window.removeEventListener('popstate', closeMenus)
  }, [closeMenus])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!mobileOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const panel = mobilePanelRef.current
    const trigger = menuButtonRef.current
    const focusable = () =>
      Array.from(
        panel?.querySelectorAll<HTMLElement>('a[href], button, input, [tabindex="0"]') ?? [],
      ).filter((element) => !element.closest('[inert]') && element.getClientRects().length > 0)
    focusable()[0]?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenus()
      if (event.key !== 'Tab') return
      const items = focusable()
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    const desktop = window.matchMedia('(min-width: 1024px)')
    const onDesktop = () => {
      if (desktop.matches) closeMenus()
    }
    desktop.addEventListener('change', onDesktop)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      desktop.removeEventListener('change', onDesktop)
      trigger?.focus()
    }
  }, [mobileOpen, closeMenus])

  useEffect(
    () => () => {
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current)
    },
    [],
  )

  const handleServicesEnter = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current)
    setServicesOpen(true)
  }

  const handleServicesLeave = () => {
    hoverTimeout.current = setTimeout(() => setServicesOpen(false), 120)
  }

  return (
    <>
      {/* ── Desktop + tablet header ── */}
      <header
        className={cn(
          'sticky top-0 z-40 w-full transition-all duration-300',
          scrolled
            ? 'bg-base-50/95 backdrop-blur-md shadow-[0_1px_16px_rgba(26,21,16,0.09)] border-b border-base-200/80'
            : 'bg-base-50 border-b border-base-200/60',
        )}
      >
        <Container
          className={cn(
            'flex items-center justify-between gap-2 transition-all duration-300 lg:gap-4',
            'h-[72px] lg:h-24',
          )}
        >
          {/* Logo / wordmark */}
          <Link
            to={ROUTES.HOME}
            className="flex items-center gap-2.5 shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
          >
            <span className="flex flex-col items-center">
              <img
                src={logo}
                alt="Instrumental Health"
                className={cn('w-auto object-contain transition-all duration-300', 'h-12 lg:h-14')}
              />
              <span className="mt-0.5 hidden whitespace-nowrap text-[0.68rem] font-medium uppercase tracking-[0.16em] text-green-800 lg:block">
                Changing the Mindset
              </span>
            </span>
            <span className="flex flex-col lg:hidden">
              <span className="font-display text-xl leading-tight text-green-900">
                Instrumental Health
              </span>
              <span className="text-xs tracking-wide text-base-500">Changing the Mindset</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav
            className="hidden lg:flex items-center gap-0 flex-1 justify-center"
            aria-label="Main navigation"
          >
            {/* Home */}
            <NavLink to={ROUTES.HOME} end className={({ isActive }) => desktopLink(isActive)}>
              Home
            </NavLink>

            {/* Services — hover dropdown */}
            <div
              className="relative"
              onMouseEnter={handleServicesEnter}
              onMouseLeave={handleServicesLeave}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setServicesOpen(false)
                  event.currentTarget.querySelector('button')?.focus()
                }
              }}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node))
                  setServicesOpen(false)
              }}
            >
              <button
                onClick={() => setServicesOpen((open) => !open)}
                className={desktopLink(servicesOpen)}
                aria-controls="desktop-services"
                aria-expanded={servicesOpen}
              >
                Services
                <LuChevronDown
                  aria-hidden="true"
                  className={cn(
                    'w-4 h-4 transition-transform duration-200',
                    servicesOpen && 'rotate-180',
                  )}
                />
              </button>

              {/* Dropdown panel — the padding-top bridges the gap so hover never drops */}
              <div
                id="desktop-services"
                inert={!servicesOpen}
                className={cn(
                  'absolute left-1/2 top-full z-50 w-[46rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 pt-3 transition-all duration-200',
                  servicesOpen
                    ? 'pointer-events-auto translate-y-0 opacity-100'
                    : 'pointer-events-none -translate-y-2 opacity-0',
                )}
              >
                <div className="grid grid-cols-[15rem_1fr] overflow-hidden rounded-3xl border border-base-200 bg-white shadow-elevated">
                  {/* Feature card */}
                  <div className="relative flex flex-col justify-end overflow-hidden bg-green-950 text-base-50">
                    <img
                      src={kandakePortrait}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover object-[50%_25%] opacity-60"
                    />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-t from-green-950 via-green-950/70 to-transparent"
                    />
                    <div className="relative p-6">
                      <span aria-hidden="true" className="mb-4 block h-px w-10 bg-gold-300/80" />
                      <p className="font-display text-2xl leading-tight">
                        Start with a herbal consultation
                      </p>
                      <p className="mt-2 text-sm leading-6 text-green-100">
                        Talk it through with Kandake and find the right next step.
                      </p>
                      <Link
                        to={`${ROUTES.CONTACT}?service=herbal-consultation`}
                        onClick={() => setServicesOpen(false)}
                        className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-base-50 px-5 text-sm font-semibold text-green-950 transition-colors hover:bg-gold-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-200"
                      >
                        Book Now
                        <LuArrowRight aria-hidden="true" className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>

                  {/* Service list */}
                  <div className="flex flex-col p-3">
                    <ul className="space-y-0.5">
                      {services.map((s) => (
                        <li key={s.path}>
                          <NavLink
                            to={s.path}
                            onClick={() => setServicesOpen(false)}
                            className={({ isActive }) =>
                              cn(
                                'group flex items-center gap-4 rounded-2xl p-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-600',
                                isActive ? 'bg-green-50' : 'hover:bg-base-50',
                              )
                            }
                          >
                            <span
                              aria-hidden="true"
                              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-700 transition-colors group-hover:bg-green-700 group-hover:text-white"
                            >
                              <s.icon className="h-5 w-5" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-[0.95rem] font-medium leading-snug text-base-900">
                                {s.label}
                              </span>
                              <span className="mt-0.5 block text-sm leading-snug text-base-600">
                                {s.desc}
                              </span>
                            </span>
                            <LuArrowRight
                              aria-hidden="true"
                              className="h-4 w-4 shrink-0 -translate-x-1 text-green-700 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100"
                            />
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={ROUTES.SERVICES}
                      onClick={() => setServicesOpen(false)}
                      className="mt-2 flex min-h-12 items-center justify-between rounded-2xl border-t border-base-200 px-4 pt-2 text-sm font-medium text-green-700 transition-colors hover:text-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-600"
                    >
                      <span className="flex items-center gap-2.5">
                        <LuLayoutGrid aria-hidden="true" className="h-4 w-4" />
                        View all services
                      </span>
                      <LuArrowRight aria-hidden="true" className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {mainNav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => desktopLink(isActive)}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* CTA + hamburger */}
          <div className="flex items-center gap-3 shrink-0">
            <Link
              to={`${ROUTES.CONTACT}?service=herbal-consultation`}
              className="hidden sm:inline-flex min-h-12 px-7 items-center text-base font-medium tracking-wide text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-full transition-colors shadow-sm"
            >
              Book Now
            </Link>

            {/* Hamburger — mobile only */}
            <button
              ref={menuButtonRef}
              aria-controls="mobile-navigation"
              className="lg:hidden flex flex-col justify-center items-center w-12 h-12 rounded-xl border border-base-200 bg-white text-green-800 hover:bg-base-100 transition-colors group"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <LuX aria-hidden="true" className="h-6 w-6" />
              ) : (
                <LuMenu aria-hidden="true" className="h-6 w-6" />
              )}
            </button>
          </div>
        </Container>
      </header>

      {/* ── Mobile overlay backdrop ── */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-base-900/40 backdrop-blur-sm lg:hidden transition-opacity duration-300',
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      {/* ── Mobile slide-in panel ── */}
      <div
        ref={mobilePanelRef}
        id="mobile-navigation"
        role="dialog"
        aria-modal={mobileOpen ? true : undefined}
        aria-label="Navigation menu"
        inert={!mobileOpen}
        className={cn(
          'fixed top-3 right-3 z-50 flex max-h-[calc(100dvh-1.5rem)] w-[calc(100%-1.5rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-base-200 bg-base-50 shadow-elevated lg:hidden motion-safe:transition-all motion-safe:duration-200',
          mobileOpen ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0 pointer-events-none',
        )}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between h-[72px] px-5 border-b border-base-200 shrink-0">
          <Link
            to={ROUTES.HOME}
            className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
            onClick={() => setMobileOpen(false)}
          >
            <img src={logo} alt="Instrumental Health" className="h-12 w-auto object-contain" />
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center w-12 h-12 rounded-lg text-base-500 hover:bg-base-100 transition-colors"
            aria-label="Close menu"
          >
            <LuX aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>

        {/* Panel nav */}
        <nav
          aria-label="Mobile navigation"
          className="min-h-0 overflow-y-auto overscroll-contain px-3 py-3 space-y-1"
        >
          {/* Home */}
          <NavLink
            to={ROUTES.HOME}
            end
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cn(
                'flex min-h-12 items-center px-4 py-2 rounded-lg text-base font-medium transition-colors',
                isActive
                  ? 'bg-green-50 text-green-700'
                  : 'text-base-700 hover:bg-base-100 hover:text-base-900',
              )
            }
          >
            Home
          </NavLink>

          {/* Services accordion */}
          <div>
            <button
              onClick={() => setMobileServicesOpen((v) => !v)}
              aria-expanded={mobileServicesOpen}
              aria-controls="mobile-services"
              className="flex min-h-12 w-full items-center justify-between px-4 py-2 rounded-lg text-base font-medium text-base-700 hover:bg-base-100 transition-colors"
            >
              Services
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
                  mobileServicesOpen ? 'bg-green-700 text-white' : 'bg-base-100 text-base-600',
                )}
              >
                <LuChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform duration-300',
                    mobileServicesOpen && 'rotate-180',
                  )}
                />
              </span>
            </button>

            <div
              id="mobile-services"
              inert={!mobileServicesOpen}
              className={cn(
                'grid transition-all duration-300 ease-out',
                mobileServicesOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
              )}
            >
              <div className="overflow-hidden">
                <ul className="mt-1 space-y-0.5 rounded-2xl bg-base-100/70 p-1.5">
                  {services.map((s) => (
                    <li key={s.path}>
                      <NavLink
                        to={s.path}
                        onClick={closeMenus}
                        className={({ isActive }) =>
                          cn(
                            'group flex min-h-12 items-center gap-3 rounded-xl px-2.5 py-2 text-[0.95rem] leading-snug transition-colors',
                            isActive
                              ? 'bg-white font-medium text-green-800 shadow-sm'
                              : 'text-base-700 hover:bg-white/70',
                          )
                        }
                      >
                        <span
                          aria-hidden="true"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-50 text-green-700"
                        >
                          <s.icon className="h-[1.125rem] w-[1.125rem]" />
                        </span>
                        {s.label}
                      </NavLink>
                    </li>
                  ))}
                  <li>
                    <Link
                      to={ROUTES.SERVICES}
                      onClick={closeMenus}
                      className="flex min-h-12 items-center justify-between rounded-xl px-3 text-sm font-medium text-green-700 transition-colors hover:bg-white/70"
                    >
                      View all services
                      <LuArrowRight aria-hidden="true" className="h-4 w-4" />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-base-200 mx-3 my-1.5" />

          {mainNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeMenus}
              className={({ isActive }) =>
                cn(
                  'flex min-h-12 items-center px-4 py-2 rounded-lg text-base font-medium transition-colors',
                  isActive
                    ? 'bg-green-50 text-green-700'
                    : 'text-base-700 hover:bg-base-100 hover:text-base-900',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* CTA pinned to bottom */}
        <div className="shrink-0 px-4 py-3 border-t border-base-200 bg-base-50">
          <Link
            to={`${ROUTES.CONTACT}?service=herbal-consultation`}
            onClick={closeMenus}
            className="block w-full py-3.5 px-4 text-center text-base font-medium text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-full transition-colors shadow-sm"
          >
            Book Now
          </Link>
        </div>
      </div>
    </>
  )
}
